import React, { useEffect, useRef, useState } from "react";
import { Send, Image as ImageIcon, Mic, Video, X, Camera, Check, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { api, auth } from "../lib/api";
import { useAgencyNavigate } from "../lib/agency";

export default function Chat() {
  const navigate = useAgencyNavigate();
  const user = auth.current();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    load();
    markRead();
    // Sin esto, una respuesta de soporte solo aparecía al recargar la página
    // a mano — Groups.jsx ya sondea su estado cada 8s, aquí faltaba lo mismo.
    const i = setInterval(() => { load(); markRead(); }, 8000);
    return () => clearInterval(i);
  }, []);

  const markRead = () => {
    api.post(`/chat/${user.booking_id}/mark-read`).catch(() => {});
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/chat/messages/${user.booking_id}`);
      setMsgs((prev) => {
        // Evita el "salto" de re-render+scroll en cada sondeo cuando no hay
        // mensajes nuevos.
        if (prev.length === data.length && prev[prev.length - 1]?.id === data[data.length - 1]?.id) {
          return prev;
        }
        setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 50);
        return data;
      });
    } catch {
      // Sondeo silencioso: un fallo pasajero no debe interrumpir el chat.
    }
  };

  const send = async (payload) => {
    const { data } = await api.post("/chat/messages", {
      booking_id: user.booking_id,
      sender: user.traveler_name,
      ...payload,
    });
    setMsgs((m) => [...m, data]);
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 50);
  };

  const onSend = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    send({ type: "text", content: text });
    setText("");
  };

  const onFile = async (e, type) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => send({ type, content: reader.result, caption: f.name });
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const toggleRecord = async () => {
    if (recording) {
      recorderRef.current?.stop();
      setRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => chunksRef.current.push(ev.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const reader = new FileReader();
        reader.onload = () => send({ type: "audio", content: reader.result, caption: "Nota de voz" });
        reader.readAsDataURL(blob);
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      recorderRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("Micrófono no disponible — revisa el permiso del navegador.");
    }
  };

  if (!user) return null;

  return (
    <Layout>
      <section className="max-w-3xl mx-auto px-4 md:px-6 py-10 flex flex-col h-[calc(100dvh-4rem)]">
        <div className="mb-6 fade-up">
          <p className="text-xs uppercase tracking-[0.4em] text-[var(--brand-cyan,#FF85AC)] mb-2">Chat Multimodal</p>
          <h1 className="font-display text-3xl font-light tracking-tight">Chat con tu Agencia</h1>
        </div>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto overscroll-contain glass rounded-3xl p-5 border border-white/10 mb-4 space-y-3"
          data-testid="chat-window"
        >
          {msgs.length === 0 && (
            <p className="text-center text-neutral-500 text-sm py-10">
              Envía tu primer mensaje. Soporte, imágenes, audio y video disponibles.
            </p>
          )}
          {msgs.map((m) => {
            const mine = m.sender_role !== "staff";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                  mine ? "bg-[var(--brand-cyan,#FF85AC)] text-black" : "bg-[#0A0A0A] border border-white/10 text-white"
                }`}>
                  <p className="text-[10px] uppercase tracking-[0.2em] opacity-60 mb-1">{m.sender}</p>
                  {m.type === "text" && <p className="text-sm leading-relaxed [overflow-wrap:anywhere]">{m.content}</p>}
                  {m.type === "image" && (
                    <img src={m.content} alt="" className="rounded-xl max-w-full" />
                  )}
                  {m.type === "audio" && <audio src={m.content} controls className="w-64 max-w-full" />}
                  {m.type === "video" && (
                    <video src={m.content} controls className="rounded-xl max-w-full max-h-72" />
                  )}
                  {m.caption && m.type !== "text" && (
                    <p className="text-xs opacity-70 mt-1">{m.caption}</p>
                  )}
                  {mine && (
                    <div className="flex justify-end mt-1" data-testid={`read-status-${m.id}`}>
                      {m.read
                        ? <CheckCheck className="w-3.5 h-3.5 opacity-70" />
                        : <Check className="w-3.5 h-3.5 opacity-50" />}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <form onSubmit={onSend} className="glass rounded-full border border-white/10 p-2 flex items-center gap-2">
          <label className="cursor-pointer p-2.5 hover:bg-white/5 rounded-full" data-testid="upload-image-btn" aria-label="Enviar imagen">
            <ImageIcon className="w-4 h-4 text-[var(--brand-cyan,#FF85AC)]" />
            <input type="file" accept="image/*" hidden onChange={(e) => onFile(e, "image")} />
          </label>
          <label className="cursor-pointer p-2.5 hover:bg-white/5 rounded-full" data-testid="camera-btn" aria-label="Tomar foto">
            <Camera className="w-4 h-4 text-[var(--brand-cyan,#FF85AC)]" />
            <input type="file" accept="image/*" capture="environment" hidden onChange={(e) => onFile(e, "image")} />
          </label>
          <label className="cursor-pointer p-2.5 hover:bg-white/5 rounded-full" data-testid="upload-video-btn" aria-label="Enviar video">
            <Video className="w-4 h-4 text-[var(--brand-cyan,#FF85AC)]" />
            <input type="file" accept="video/*" hidden onChange={(e) => onFile(e, "video")} />
          </label>
          <button
            type="button"
            onClick={toggleRecord}
            data-testid="record-audio-btn"
            aria-label={recording ? "Detener grabación" : "Grabar nota de voz"}
            className={`p-2.5 rounded-full ${recording ? "bg-[#FF3B30] text-white sos-active" : "hover:bg-white/5 text-[var(--brand-cyan,#FF85AC)]"}`}
          >
            {recording ? <X className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Escribe un mensaje…"
            data-testid="chat-input"
            aria-label="Escribe un mensaje"
            className="flex-1 bg-transparent outline-none px-3 text-sm text-white placeholder:text-neutral-600"
          />
          <button type="submit" data-testid="send-msg-btn" aria-label="Enviar mensaje" className="bg-[var(--brand-cyan,#FF85AC)] text-black p-2.5 rounded-full hover:bg-[#FF9DC0]">
            <Send className="w-4 h-4" />
          </button>
        </form>
      </section>
    </Layout>
  );
}
